/* eslint-disable i18next/no-literal-string */
import React, { FunctionComponent, useState } from 'react'

import styled from '@emotion/styled'
import Head from 'next/head'
import Cookies from 'universal-cookie'

import { AlertOutlineIcon, BorrowIcon, CommPhoneFillIcon, ExpandIcon } from '@arcade/web/icons/react'
import { ArcadeButton, spacing, Stack, Text } from '@cash-design-system/react'

import { locationContextParamName, LocationContexts, QUERY_PARAM, Route, sessionCookieName } from '../../model'
import { persistor } from '../../state'
import { getAppInfo } from '../../utils'
import { getAppConfig } from '../../utils/config'
import { Card } from '../Card'

const StyledDevTools = styled.div`
  margin: 16px 0 0;
  display: grid;
  grid-template-rows: 60px auto;
`

const StyledButtonList = styled.ul`
  list-style-type: none;
  margin: -0.2rem;
`

const StyledButtonListItem = styled.li`
  margin: 0.2rem;
`

const { debugMode } = getAppConfig()

const AppVersionDebugger = () => {
  const appInfo = getAppInfo()

  return (
    <div>
      AppVersion:{appInfo?.appVersion} BuildNumber:{appInfo?.buildNumber}
    </div>
  )
}

const RestartButton = ({ onPurge }: { onPurge: () => void }) => (
  <ArcadeButton size="compact" prominence="standard" onClick={onPurge}>
    <BorrowIcon />
    &nbsp; Restart
  </ArcadeButton>
)

const SilentAuthEmbedButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => (
  <ArcadeButton size="compact" prominence="standard" disabled={disabled} onClick={onClick}>
    <CommPhoneFillIcon />
    &nbsp; Embed Silent Auth
  </ArcadeButton>
)

const SilentAuthEmbedFailureButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => (
  <ArcadeButton size="compact" prominence="standard" disabled={disabled} onClick={onClick}>
    <AlertOutlineIcon />
    &nbsp; Silent Auth Failure
  </ArcadeButton>
)

const SilentAuthEmbedErrorButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => (
  <ArcadeButton size="compact" prominence="standard" disabled={disabled} onClick={onClick}>
    <AlertOutlineIcon />
    &nbsp; Silent Auth Error
  </ArcadeButton>
)

const ToggleSUPLocationButton = ({ onToggle }: { onToggle: () => void }) => {
  const inSUP = new Cookies().get(locationContextParamName) === LocationContexts.SUP
  return (
    <ArcadeButton size="compact" prominence="standard" onClick={onToggle}>
      <AlertOutlineIcon />
      &nbsp; {inSUP ? 'Reset location' : 'Use SUP location'}
    </ArcadeButton>
  )
}

const DevToolActions = ({
  onPurge,
  onEmbedSilentAuth,
  onEmbedSilentAuthFailure,
  onEmbedSilentAuthError,
  onToggleSUPLocation,
  isUsingSilentAuth,
}: {
  onPurge: () => void
  onEmbedSilentAuth: () => void
  onEmbedSilentAuthFailure: () => void
  onEmbedSilentAuthError: () => void
  onToggleSUPLocation: () => void
  isUsingSilentAuth: boolean
}) => (
  <StyledButtonList>
    <StyledButtonListItem>
      <RestartButton onPurge={onPurge} />
    </StyledButtonListItem>
    <StyledButtonListItem>
      <SilentAuthEmbedButton disabled={isUsingSilentAuth} onClick={onEmbedSilentAuth} />
    </StyledButtonListItem>
    <StyledButtonListItem>
      <SilentAuthEmbedFailureButton disabled={!isUsingSilentAuth} onClick={onEmbedSilentAuthFailure} />
    </StyledButtonListItem>
    <StyledButtonListItem>
      <SilentAuthEmbedErrorButton disabled={!isUsingSilentAuth} onClick={onEmbedSilentAuthError} />
    </StyledButtonListItem>
    <StyledButtonListItem>
      <ToggleSUPLocationButton onToggle={onToggleSUPLocation} />
    </StyledButtonListItem>
    <StyledButtonList>
      <AppVersionDebugger />
    </StyledButtonList>
  </StyledButtonList>
)

export const DevTools: FunctionComponent = () => {
  const [open, setOpen] = useState(false)

  const searchParams = new URLSearchParams(window.location.search)
  const isUsingSilentAuth = Boolean(searchParams.get('useSilentAuth') || '')

  const getRestartUrl = (): string => {
    const checkoutRequestId = `DEV_${Math.random().toString(36).slice(2, 7)}`
    return `${Route.ENTRY}?${QUERY_PARAM.CUSTOMER_REQUEST_ID}=${checkoutRequestId}`
  }

  const handleEmbedSilentAuth = () => {
    searchParams.set('useSilentAuth', 'true')
    window.location.search = searchParams.toString()
  }

  const handlePurge = async (): Promise<void> => {
    const restartUrl = getRestartUrl()
    persistor.pause()
    await persistor.flush()
    await persistor.purge()
    setTimeout(() => {
      window.location.href = restartUrl
    })
  }

  const handleDevTools = () => {
    setOpen(!open)
  }

  const silentAuthEventHandlers = `
  window.CashAppNative = window.CashAppNative || {}

    window.CashAppNative.authorizeCustomerRequest = function (mobileUrl) {
      console.log('%cSilent Auth URL', 'background:tomato;color:white;padding:3px;font-weight:bold', mobileUrl);
    }
  `

  const handleEmbedSilentAuthFailure = () => {
    window.CashApp?.pay?._native?.dispatchEvent(
      new CustomEvent('AUTHORIZE_RESPONSE', {
        detail: {
          statusCode: '400',
          errorTitle: '',
          errorMessage: '',
        },
      })
    )
  }

  const handleEmbedSilentAuthError = () => {
    window.CashApp?.pay?._native?.dispatchEvent(
      new CustomEvent('AUTHORIZE_RESPONSE', {
        detail: {
          statusCode: '500',
          errorTitle: '',
          errorMessage: '',
        },
      })
    )
  }

  const handleToggleSUPLocation = () => {
    const cookies = new Cookies()
    const turnOff = cookies.get(locationContextParamName) === LocationContexts.SUP
    if (turnOff) {
      cookies.remove(locationContextParamName)
      cookies.remove(sessionCookieName)
    } else {
      cookies.set(locationContextParamName, LocationContexts.SUP)
      cookies.set(sessionCookieName, 'sup-session-cookie')
    }
    handlePurge()
  }

  return debugMode ? (
    <>
      {isUsingSilentAuth && (
        <Head>
          <script type="text/javascript">{silentAuthEventHandlers}</script>
        </Head>
      )}
      <StyledDevTools>
        <ArcadeButton prominence="subtle" size="compact" onClick={handleDevTools}>
          {open ? `Close` : `Open`} Dev Tools <ExpandIcon size={24} />
        </ArcadeButton>
        {open && (
          <Card>
            <Stack space={spacing.m}>
              <Text typography="tabTitle" color="textStandard" as="h3">
                Dev Tools
              </Text>
              <DevToolActions
                onPurge={handlePurge}
                onEmbedSilentAuth={handleEmbedSilentAuth}
                onEmbedSilentAuthFailure={handleEmbedSilentAuthFailure}
                onEmbedSilentAuthError={handleEmbedSilentAuthError}
                onToggleSUPLocation={handleToggleSUPLocation}
                isUsingSilentAuth={isUsingSilentAuth}
              />
            </Stack>
          </Card>
        )}
      </StyledDevTools>
    </>
  ) : null
}
